import React, { useMemo, useState } from 'react';
import {
  Box,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Button } from 'src/components/atoms/Button';
import { CustomToast } from 'src/components/atoms/CustomToast';
import { useCustomToast } from 'src/hooks/useCustomToast';
import { UserRole, useAuthContext } from 'src/contexts/AuthContext';
import { dayjs } from 'src/libs/dayjs';
import {
  AdminInvite,
  AdminInviteStatus,
  RefreshFrequency,
  SubscriptionTariff,
  useAdminInvitesQuery,
  useCancelAdminInviteMutation,
  useInviteCompanyAdminMutation,
  useResendAdminInviteMutation,
} from 'src/api/adminInvites';
import { InviteCompanyAdminModal } from 'src/features/AdminInviteUsers/components/molecules/InviteCompanyAdminModal';

const statusLabel: Record<AdminInviteStatus, string> = {
  invited: 'Invited',
  expired: 'Expired',
  accepted: 'Accepted',
  cancelled: 'Cancelled',
};

const statusColor: Record<AdminInviteStatus, string> = {
  invited: 'yellow.600',
  expired: 'red.500',
  accepted: 'icon.green',
  cancelled: 'gray.500',
};

// Surface most-actionable rows first: pending invites, then expired ones the
// staff member can resend, then archival accepted/cancelled rows.
const statusOrder: Record<AdminInviteStatus, number> = {
  invited: 0,
  expired: 1,
  accepted: 2,
  cancelled: 3,
};

const formatSentAt = (iso: string): string => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY HH:mm') : iso;
};

const errorMessageOf = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const message = (error as { response?: { data?: { message?: string } } })
      .response?.data?.message;
    if (typeof message === 'string') return message;
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
};

export const AdminInviteUsers: React.FC = () => {
  const { userProfile } = useAuthContext();
  const isServiceAdmin = userProfile?.role === UserRole.ADMIN;

  const { toastState, showSuccess, showError, showInfo, hideToast } =
    useCustomToast();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // Per-row disable: tracks ids of invites with an in-flight resend/cancel.
  // More robust than `mutation.variables === row.id`, which only reflects the
  // most recent variables and breaks if these hooks are ever merged.
  const [mutatingIds, setMutatingIds] = useState<Set<string>>(new Set());

  const invitesQuery = useAdminInvitesQuery();
  const inviteMutation = useInviteCompanyAdminMutation();
  const resendMutation = useResendAdminInviteMutation();
  const cancelMutation = useCancelAdminInviteMutation();

  const rows = useMemo<AdminInvite[]>(() => {
    const list = invitesQuery.data ?? [];
    return [...list].sort((a, b) => {
      const byStatus = statusOrder[a.status] - statusOrder[b.status];
      if (byStatus !== 0) return byStatus;
      return new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime();
    });
  }, [invitesQuery.data]);

  if (!isServiceAdmin) {
    return (
      <Box p={10}>
        <Text fontSize="lg" color="content.label">
          Access denied. This page is restricted to Yapamar staff.
        </Text>
      </Box>
    );
  }

  const markMutating = (id: string) =>
    setMutatingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const unmarkMutating = (id: string) =>
    setMutatingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const handleCreate = (data: {
    email: string;
    companyName: string;
    displayName?: string;
    subscriptionRefreshFrequency: RefreshFrequency;
    subscriptionTariff: SubscriptionTariff;
    subscriptionOfflineMediaAvailable: boolean;
    subscriptionModelsAvailable: number;
    subscriptionUntilDue: string;
  }) => {
    setCreateError(null);
    inviteMutation.mutate(data, {
      onSuccess: (invite) => {
        showSuccess(
          'Invite sent',
          `${invite.email} can now sign up as admin of ${invite.companyName}.`,
        );
        setIsInviteModalOpen(false);
      },
      onError: (error) => setCreateError(errorMessageOf(error)),
    });
  };

  const handleResend = (invite: AdminInvite) => {
    markMutating(invite.id);
    resendMutation.mutate(invite.id, {
      onSuccess: (fresh) => {
        showSuccess('Invite resent', `Fresh link sent to ${fresh.email}.`);
      },
      onError: (error) => {
        showError('Could not resend', errorMessageOf(error));
      },
      onSettled: () => unmarkMutating(invite.id),
    });
  };

  const handleOpenInviteModal = () => {
    setCreateError(null);
    setIsInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setCreateError(null);
  };

  const handleCancel = (invite: AdminInvite) => {
    markMutating(invite.id);
    cancelMutation.mutate(invite.id, {
      onSuccess: () => {
        showInfo('Invite cancelled', `Invite for ${invite.email} cancelled.`);
      },
      onError: (error) => {
        showError('Could not cancel', errorMessageOf(error));
      },
      onSettled: () => unmarkMutating(invite.id),
    });
  };

  return (
    <Box p={10}>
      <HStack justify="space-between" align="center" mb={2}>
        <Text fontSize="24px" fontWeight="bold">
          Invite customer admins
        </Text>
        <Button
          buttonName="open-invite-admin-modal"
          bg="icon.green"
          color="white"
          _hover={{ bg: 'icon.green', opacity: 0.9 }}
          onClick={handleOpenInviteModal}>
          Invite admin
        </Button>
      </HStack>
      <Text fontSize="sm" color="content.muted" mb={6}>
        Send invitation links that let new customer admins create their company
        in Yapamar. Invites expire after 48 hours.
      </Text>

      {invitesQuery.isLoading ? (
        <HStack justify="center" py={10}>
          <Spinner speed="0.8s" size="xl" thickness="4px" />
        </HStack>
      ) : invitesQuery.isError ? (
        <Text color="red.500">
          Could not load invites: {errorMessageOf(invitesQuery.error)}
        </Text>
      ) : rows.length === 0 ? (
        <Text color="content.muted">No invites yet.</Text>
      ) : (
        <Table variant="simple" size="md">
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Email</Th>
              <Th>Company</Th>
              <Th>Display name</Th>
              <Th>Sent</Th>
              <Th>Status</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, index) => {
              const canResend =
                row.status === 'invited' || row.status === 'expired';
              const canCancel = row.status === 'invited';
              const isMutatingThisRow = mutatingIds.has(row.id);
              return (
                <Tr key={row.id}>
                  <Td>{index + 1}</Td>
                  <Td>{row.email}</Td>
                  <Td>{row.companyName}</Td>
                  <Td>{row.displayName ?? '—'}</Td>
                  <Td>{formatSentAt(row.sentAt)}</Td>
                  <Td>
                    <Text color={statusColor[row.status]}>
                      {statusLabel[row.status]}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      {canResend && (
                        <Button
                          buttonName={`resend-${row.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleResend(row)}
                          isDisabled={isMutatingThisRow}>
                          Resend
                        </Button>
                      )}
                      {canCancel && (
                        <Button
                          buttonName={`cancel-${row.id}`}
                          size="sm"
                          variant="outline"
                          color="red.500"
                          borderColor="red.300"
                          _hover={{ bg: 'red.50' }}
                          onClick={() => handleCancel(row)}
                          isDisabled={isMutatingThisRow}>
                          Cancel
                        </Button>
                      )}
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      <InviteCompanyAdminModal
        isOpen={isInviteModalOpen}
        onClose={handleCloseInviteModal}
        onSubmit={handleCreate}
        isLoading={inviteMutation.isLoading}
        errorMessage={createError}
      />

      <CustomToast
        isOpen={toastState.isOpen}
        title={toastState.title}
        description={toastState.description}
        type={toastState.type}
        onClose={hideToast}
        position={{ top: '90px', right: '20px' }}
      />
    </Box>
  );
};
